import { DataPointArray } from '@evorait/pace-core';
import { createAction, props } from '@ngrx/store';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentId } from '../../types/content-block';
import { AstoriaOption } from '../../types/option';
import { REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION } from '../../types/replacement-or-last-procedure-date';
import { Signature } from '../../types/signature';

export const updateFunctionalElementSelection = createAction(
    '[Forms] Update checkpoint selection in functional element',
    props<{
        id: ContentId; // Content id of the functional element
        selectedChoiceXmlId: string | undefined; // XML id of the selected choice
    }>(),
);

export const updateDiscrepancyLog = createAction(
    '[Forms] Update discrepancy log',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateSignature = createAction(
    '[Forms] Updated signature',
    props<{
        id: ContentId;
        signatureType: Signature['type'];
        dateXmlId: string;
        date: string;
        newSignature: string;
        signatureDataPoints: DataPointArray;
    }>(),
);

export const updateSignatureSuccess = createAction('[Forms] Successfully updated signature');

export const updateSignatureName = createAction(
    '[Forms] Updated signee name',
    props<{
        id: ContentId;
        signatureType: Signature['type'];
        xmlId: string;
        firstName: string;
        lastName: string;
        email: string;
    }>(),
);

export const updateSystemConditionSignatureName = createAction(
    '[Forms] Update signee name in system condition',
    props<{
        signatureType: Signature['type'];
        xmlId: string;
        firstName: string;
        lastName: string;
    }>(),
);

export const updateSystemConditionSignature = createAction(
    '[Forms] Updated signature in system condition',
    props<{
        signatureType: Signature['type'];
        dateXmlId: string;
        date: string;
        newSignature: string;
        signatureDataPoints: DataPointArray;
    }>(),
);

export const updateSystemConditionSignatureDisabled = createAction(
    '[Forms] Updated disabled state of signature in system condition',
    props<{
        signatureType: Signature['type'];
        disabled: boolean;
    }>(),
);

export const updateMaterialNumber = createAction(
    '[Forms] Updated material number',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateSerialNumber = createAction(
    '[Forms] Updated serial number',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateDescription = createAction(
    '[Forms] Updated description',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateMaterialSerialNumber = createAction(
    '[Forms] Updated material serial number',
    props<{
        id: ContentId;
        materialXmlId: string;
        materialValue: string;
        serialXmlId: string;
        serialValue: string;
        descriptionXmlId: string;
        descriptionValue: string;
    }>(),
);

export const updateDatePerformed = createAction(
    '[Forms] Updated date performed',
    props<{
        id: ContentId;
        dayXmlId?: string; // used for astoria
        monthXmlId?: string; // used for astoria
        yearXmlId?: string; // used for astoria
        dateXmlId?: string; // used for cosima
        // would pass moment.Moment for newValue but it's not serializable by ngrx
        newValue: string;
    }>(),
);

export const updateReplacementOrLastProcedureDateSelection = createAction(
    '[Forms] Updated replacement or last procedure date selection',
    props<{
        id: ContentId;
        selected: REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION;
        xmlId: string;
    }>(),
);

export const updateReplacementOrLastProcedureDateValue = createAction(
    '[Forms] Updated replacement or last procedure date value',
    props<{
        id: ContentId;
        dayXmlId?: string; // used for astoria
        monthXmlId?: string; // used for astoria
        yearXmlId?: string; // used for astoria
        dateXmlId?: string; // used for cosima
        // would pass moment.Moment for newValue but it's not serializable by ngrx
        newValue: string;
    }>(),
);

export const updateValue = createAction(
    '[Forms] Update value',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
        valueXmlId?: string;
        valid: boolean;
    }>(),
);

export const updateAstoriaOption = createAction(
    '[Forms] Update astoria option functional element',
    props<{
        id: ContentId;
        xmlId: string;
        selected: AstoriaOption['selected'];
    }>(),
);

export const updateCosimaOption = createAction(
    '[Forms] Update cosima option functional element',
    props<{
        id: ContentId;
        selectedChoiceXmlId: string | undefined;
    }>(),
);

export const updateFunctionalElementRemarks = createAction(
    '[Forms] Update functional element remarks',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateMaterialSwapIn = createAction(
    '[Forms] Update material swap in number',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateSerialSwapIn = createAction(
    '[Forms] Update serial swap in number',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateMaterialSwapOut = createAction(
    '[Forms] Update material swap out number',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateSerialSwapOut = createAction(
    '[Forms] Update serial swap out number',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateRepairTime = createAction(
    '[Forms] Update repair time',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: string;
    }>(),
);

export const autofillFunctionalElements = createAction(
    '[Forms] Autofill functional elements',
    props<{
        id: ContentId;
        selected: CHOICE_TYPE;
    }>(),
);

export const updateFunctionalElementsChoicesSelection = createAction(
    '[Forms] Update selection for functional elements',
    props<{
        ids: ContentId[];
        selected: CHOICE_TYPE;
    }>(),
);

export const updateRemarksSection = createAction(
    '[Forms] Update remarks section',
    props<{
        xmlId: string;
        newValue: string;
    }>(),
);

export const updateOptionSet = createAction(
    '[Forms] Update optionSet',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: boolean;
    }>(),
);

export const updateDropDownOptions = createAction(
    '[Forms] Update dropDownOptions',
    props<{
        id: ContentId;
        xmlId: string;
        newValue: boolean;
    }>(),
);
